import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { appConfig } from '../../config/app';
import { sharedConfig } from '../../config/shared';

const Terms = () => {
  return (
    <div className="min-vh-100 py-5 legal-page-container">
      <Container>
        <Row className="justify-content-center">
          <Col lg={10} xl={10}>
            {/* Header */}
            <div className="text-center mb-5">
              <h1 className="display-4 fw-bold text-white mb-3">Terms of Service</h1>
              <p className="lead text-light">
                Last updated: September 5, 2025
              </p>
            </div>

            {/* Main Content */}
            <Card className="glass-card border-0 shadow-lg legal-page-card">
              <Card.Body className="p-4 p-lg-5">
                <div className="legal-content">
                  {/* Acceptance of Terms */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">1. Acceptance of Terms</h2>
                    <p className="text-light">
                      By accessing and using {sharedConfig.brand.name} ("the Service"), operated by {sharedConfig.company.companyName} DBA {sharedConfig.brand.name} ("we," "us," or "our"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                    </p>
                  </section>

                  {/* Service Description */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">2. Service Description</h2>
                    <p className="text-light">
                      {sharedConfig.brand.name} provides personalized weekly fantasy football recap videos generated using Artificial Intelligence (AI) technology. Our service analyzes your fantasy football league's performance and generates entertaining recap content that celebrates winners, roasts losers, and highlights the most exciting moments from your league.
                    </p>
                    <p className="text-light">
                      We currently support fantasy football platforms, though we do not have a formal relationship with these platforms. Videos are approximately {sharedConfig.product.videoLength} in length.
                    </p>
                  </section>

                  {/* AI-Generated Content */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">3. AI-Generated Content</h2>
                    <p className="text-light mb-3">
                      <strong>Important Notice:</strong> Our weekly recap videos and scripts are generated using Artificial Intelligence (AI)  technology. These videos are not reviewed by human editors before being delivered to users.
                    </p>
                    <p className="text-light mb-3">
                      While we strive for accuracy and entertainment value, there is a risk of errors, inaccuracies, or inappropriate content in AI-generated videos. If you encounter any issues, concerns, or believe there is inappropriate content, please notify us immediately at {appConfig.contact.primary.email}.
                    </p>
                    <p className="text-light">
                      We will review reported issues and take appropriate action, which may include content removal, corrections, or other remedial measures. Your feedback helps us improve our AI systems and  maintain quality standards.
                    </p>
                  </section>

                  {/* User Accounts & Responsibilities */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">4. User Accounts & Responsibilities</h2>
                    <p className="text-light mb-3">You are responsible for:</p>
                    <ul className="text-light">
                      <li>Maintaining the confidentiality of your account credentials</li>
                      <li>All activities that occur under your account</li>
                      <li>Providing accurate and complete information during registration</li>
                      <li>Notifying us immediately of any unauthorized use of your account</li>
                      <li>Ensuring you are at least 18 years old and legally eligible to use our service</li>
                    </ul>
                    <p className="text-light">
                      You should not share your account with others or use another person's account without permission.
                    </p>
                  </section>

                  {/* Acceptable Use Policy */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">5. Acceptable Use Policy</h2>
                    <p className="text-light mb-3">You agree not to:</p>
                    <ul className="text-light">
                      <li>Use the service for any illegal or unauthorized purpose</li>
                      <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                      <li>Interfere with or disrupt the service or servers</li>
                      <li>Share content that is offensive, harmful, or violates others' rights</li>
                      <li>Use the service to harass, abuse, or harm others</li>
                      <li>Attempt to reverse engineer or copy our technology</li>
                    </ul>
                  </section>

                  {/* Payment Terms & Subscriptions */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">6. Payment Terms & Subscriptions</h2>
                    <p className="text-light mb-3">
                      Our service operates on a subscription basis. By subscribing, you agree to pay the applicable 
                      fees for your chosen plan. All fees are non-refundable except as stated in our refund policy.
                    </p>
                    <p className="text-light mb-3">
                      <strong>Billing:</strong> Subscriptions are billed in advance on a monthly basis. You authorize us 
                      to charge your payment method for all fees associated with your subscription.
                    </p>
                    <p className="text-light mb-3">
                      <strong>Price Changes:</strong> We may change our pricing with 30 days' notice. Price changes will 
                      not affect your current billing cycle but will apply to subsequent renewals.
                    </p>
                    <p className="text-light">
                      <strong>Cancellation:</strong> You may cancel your subscription at any time through your account 
                      settings. Cancellation will take effect at the end of your current billing period.
                    </p>
                  </section>

                  {/* Refund Policy */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">7. Refund Policy</h2>
                    <p className="text-light mb-3">
                      Refunds can be requested in writing via email to {appConfig.contact.primary.email}. All refund requests 
                      will be handled on a case-by-case basis.
                    </p>
                    <p className="text-light">
                      If services have already been delivered (e.g., weekly recap videos), we reserve the right to 
                      charge a pro-rated amount for those services already rendered due to the costs we would incur. 
                      This includes AI processing costs, video generation, and delivery infrastructure expenses.
                    </p>
                  </section>

                  {/* Intellectual Property Rights */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">8. Intellectual Property Rights</h2>
                    <p className="text-light mb-3">
                      <strong>Our Content:</strong> The {sharedConfig.brand.name} platform, including its design, technology, and AI systems, is owned by {sharedConfig.company.companyName} and protected by United States and Texas intellectual property laws.
                    </p>
                    <p className="text-light mb-3">
                      <strong>Your Videos:</strong> Weekly recap videos generated for your league are provided for your 
                      personal use and sharing within your league. You may share these videos on social media, 
                      messaging platforms, or other communication channels.
                    </p>
                    <p className="text-light">
                      <strong>Fantasy Data:</strong> We do not claim ownership of your fantasy football data. We only use this data to generate your personalized recap videos.
                    </p>
                  </section>

                  {/* Privacy & Data Protection */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">9. Privacy & Data Protection</h2>
                    <p className="text-light">
                      Your privacy is important to us. Our collection and use of your information is governed by our 
                      <Link to="/privacy" className="text-primary text-decoration-none"> Privacy Policy</Link>. By using our service, you consent to the collection and use of information as described in our <Link to="/privacy" className="text-primary text-decoration-none"> Privacy Policy</Link>.
                    </p>
                  </section>

                  {/* Service Availability & Limitations */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">10. Service Availability & Limitations</h2>
                    <p className="text-light mb-3">
                      We strive to provide reliable service but cannot guarantee uninterrupted availability. The service 
                      may be temporarily unavailable due to:
                    </p>
                    <ul className="text-light">
                      <li>Scheduled maintenance and updates</li>
                      <li>Technical difficulties or system failures</li>
                      <li>Third-party service disruptions</li>
                      <li>Force majeure events beyond our control</li>
                    </ul>
                    <p className="text-light">
                      We are not responsible for any losses or damages resulting from service interruptions.
                    </p>
                  </section>

                  {/* Termination */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">11. Termination</h2>
                    <p className="text-light mb-3">
                      We may terminate or suspend your account and access to the service at any time, with or without 
                      cause, with or without notice, effective immediately.
                    </p>
                    <p className="text-light">
                      Upon termination, your right to use the service will cease immediately. If you wish to terminate 
                      your account, you may simply discontinue using the service or contact us to delete your account.
                    </p>
                  </section>

                  {/* Limitation of Liability */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">12. Limitation of Liability</h2>
                    <p className="text-light">
                      To the maximum extent permitted by law, {sharedConfig.company.companyName} shall not be liable for any 
                      indirect, incidental, special, consequential, or punitive damages, including without limitation, 
                      loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the service.
                    </p>
                  </section>

                  {/* Indemnification */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">13. Indemnification</h2>
                    <p className="text-light">
                      You agree to defend, indemnify, and hold harmless {sharedConfig.company.companyName} and its officers, 
                      directors, employees, and agents from and against any claims, damages, obligations, losses, 
                      liabilities, costs, or debt arising from your use of the service or violation of these terms.
                    </p>
                  </section>

                  {/* Governing Law & Disputes */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">14. Governing Law & Disputes</h2>
                    <p className="text-light mb-3">
                      These Terms of Service shall be governed by and construed in accordance with the laws of the United States and Texas.
                    </p>
                    <p className="text-light">
                      Any disputes arising from these terms or your use of the service shall be resolved through good faith negotiation. If resolution cannot be reached, disputes may be submitted to binding  arbitration in accordance with applicable law.
                    </p>
                  </section>

                  {/* Changes to Terms */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">15. Changes to Terms</h2>
                    <p className="text-light">
                      We reserve the right to modify these terms at any time. We will notify users of any material 
                      changes by posting the updated terms on our website and updating the "Last updated" date. 
                      Your continued use of the service after changes constitutes acceptance of the updated terms.
                    </p>
                  </section>

                  {/* Contact Information */}
                  <section className="mb-0">
                    <h2 className="h3 text-white mb-3">16. Contact Information</h2>
                    <p className="text-light">
                      If you have any questions about these Terms of Service, please contact us:
                    </p>
                    <div className="bg-light bg-opacity-10 p-3 rounded">
                      <p className="text-light mb-1">
                        <strong>Email:</strong> {appConfig.contact.primary.email}
                      </p>
                      <p className="text-light mb-1">
                        <strong>Company:</strong> {sharedConfig.company.companyName} DBA {sharedConfig.brand.name}
                      </p>
                      <p className="text-light mb-0">
                        <strong>Website:</strong> {sharedConfig.brand.websiteUrl}
                      </p>
                    </div>
                  </section>
                </div>
              </Card.Body>
            </Card>

            {/* Back to Home Link */}
            <div className="text-center mt-4">
              <Link to="/" className="btn btn-outline-light">
                ← Back to Home
              </Link>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Terms;

